import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, Plus, Check, CaretLeft, CaretRight, Star, Trophy } from "@phosphor-icons/react";
import { formatDuration, formatViews } from "@/lib/api";
import { onImgError } from "@/lib/fallback";
import { useWatchLater } from "@/hooks/useWatchLater";

const ROTATE_MS = 7000;
const NON_GENRE = new Set(["trailer", "hollywood", "full movie", "movie", "classic"]);
const genreOf = (v) => {
  const g = (v.keywords || []).find((k) => !NON_GENRE.has(String(k).toLowerCase()));
  return g ? g.replace(/(^|[\s-])\w/g, (c) => c.toUpperCase()) : "Feature Film";
};

const RatingChip = ({ rating }) => (
  <span data-testid="hero-rating-chip" className="inline-flex items-center gap-1 border border-amber/70 bg-amber/15 px-2 py-0.5 font-bold text-amber shadow-[0_0_14px_rgba(255,176,0,0.3)]">
    <Star size={11} weight="fill" /> {Number(rating).toFixed(1)} <span className="font-normal text-amber/70">TMDb</span>
  </span>
);

export const FeaturedHero = ({ slides = [] }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const { isSaved, toggle } = useWatchLater();

  const count = slides.length;
  const active = count ? slides[Math.min(index, count - 1)] : null;

  const go = useCallback((next) => {
    setIndex(() => ((next % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    timer.current = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [count, paused, index]);

  if (!active) return null;

  const label = genreOf(active);
  const saved = isSaved(active.video_id);
  const cleanTitle = (active.title || "")
    .replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return (
    <section
      className="relative w-full min-h-[74vh] sm:min-h-[86vh] overflow-hidden"
      data-testid="featured-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Stacked crossfading backdrops (full-bleed, cinematic) */}
      <div className="absolute inset-0">
        {slides.map((s, i) => (
          <img
            key={s.video_id}
            src={s.default_thumb}
            alt=""
            onError={onImgError}
            className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-[1400ms] ease-out"
            style={{ opacity: i === index ? 1 : 0 }}
          />
        ))}
      </div>
      {/* Layered scrims — bottom-heavy for seamless blend into the rows, plus a left fade */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 via-35% to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent sm:to-[#050505]/0" />

      {/* Content anchored bottom-left */}
      <div className="relative z-10 mx-auto flex h-full min-h-[74vh] sm:min-h-[86vh] max-w-[1600px] min-[2200px]:max-w-[2200px] items-end px-5 sm:px-8 pb-14 sm:pb-20">
        <div key={active.video_id} className="rv-fade max-w-2xl">
          <p className="mb-3 flex items-center gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-amber" data-testid="hero-genre-label">
            <Trophy size={14} weight="fill" /> <span data-testid="hero-top-rated">Top Rated</span> <span className="text-crimson">·</span> {label}
          </p>
          <h1
            data-testid="hero-title"
            className="font-serif text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-white line-clamp-2 break-words"
            style={{ textShadow: "0 4px 40px rgba(0,0,0,0.7)" }}
          >
            {cleanTitle}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-2.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-white/70">
            {active.rating ? <RatingChip rating={active.rating} /> : null}
            {active.is_discovery ? (
              <span data-testid="hero-trailer-tag" className="border border-white/30 px-1.5 py-0.5 font-bold text-white/85">Trailer</span>
            ) : (
              <span data-testid="hero-free-tag" className="bg-emerald-500 px-1.5 py-0.5 font-bold text-black">Free</span>
            )}
            {active.views >= 1000 && (
              <>
                <span>{formatViews(active.views)} {active.views === 1 ? "view" : "views"}</span>
                <span className="text-crimson">•</span>
              </>
            )}
            <span>{formatDuration(active.duration_sec)}</span>
            {active.release_year ? (
              <>
                <span className="text-crimson">•</span>
                <span>{active.release_year}</span>
              </>
            ) : null}
          </div>

          <div className="mt-7 flex items-center gap-3">
            <Link
              to={`/watch/${active.video_id}`}
              data-testid="hero-watch-btn"
              tabIndex={0}
              className="inline-flex items-center gap-2 bg-amber px-6 sm:px-8 py-3 font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 focus:ring-offset-[#050505]"
            >
              <Play size={18} weight="fill" /> {active.is_discovery ? "Watch Trailer" : "Watch Now"}
            </Link>
            <button
              data-testid="hero-watchlist-btn"
              tabIndex={0}
              onClick={() => toggle(active.video_id)}
              aria-label={saved ? "Remove from My List" : "Add to My List"}
              className="inline-flex items-center gap-2 border border-white/25 bg-white/10 px-5 sm:px-6 py-3 font-mono text-xs sm:text-sm uppercase tracking-[0.15em] text-white backdrop-blur-md transition-colors duration-300 hover:border-amber hover:text-amber focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 focus:ring-offset-[#050505]"
            >
              {saved ? <Check size={18} weight="bold" /> : <Plus size={18} weight="bold" />}
              <span className="hidden sm:inline">My List</span>
            </button>
          </div>

          {count > 1 && (
            <div className="mt-8 flex items-center gap-4">
              <button
                data-testid="hero-prev"
                aria-label="Previous featured film"
                onClick={() => go(index - 1)}
                className="flex h-9 w-9 items-center justify-center border border-white/15 text-white/70 transition-colors duration-300 hover:border-amber hover:text-amber focus:outline-none focus:ring-2 focus:ring-amber"
              >
                <CaretLeft size={16} weight="bold" />
              </button>
              <div className="flex items-center gap-2" role="tablist">
                {slides.map((s, i) => (
                  <button
                    key={s.video_id}
                    data-testid={`hero-dot-${i}`}
                    aria-label={`Go to featured film ${i + 1}`}
                    aria-selected={i === index}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 transition-all duration-300 ${
                      i === index ? "w-8 bg-amber" : "w-4 bg-white/25 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
              <button
                data-testid="hero-next"
                aria-label="Next featured film"
                onClick={() => go(index + 1)}
                className="flex h-9 w-9 items-center justify-center border border-white/15 text-white/70 transition-colors duration-300 hover:border-amber hover:text-amber focus:outline-none focus:ring-2 focus:ring-amber"
              >
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
